import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { ApiClientError } from '@/lib/api-client';
import {
  createFirstValuation,
  fetchFirstValuation,
  fetchFirstValuations,
  finalizeFirstValuation,
  lockFirstValuation,
  requestRevaluation,
  submitValuation,
} from './api';
import type {
  CreateFirstValuationInput,
  FinalizeInput,
  FirstValuationDto,
  SubmitInput,
} from './types';

export const FIRST_VALUATION_QUERY_KEY = 'first-valuation' as const;

const listKey = (batchId: string | undefined) =>
  [FIRST_VALUATION_QUERY_KEY, 'list', batchId] as const;
const detailKey = (id: string | undefined) =>
  [FIRST_VALUATION_QUERY_KEY, 'detail', id] as const;

export const useFirstValuationList = (batchId: string | undefined) => {
  return useQuery<FirstValuationDto[], ApiClientError>({
    queryKey: listKey(batchId),
    queryFn: () => fetchFirstValuations(batchId!),
    enabled: !!batchId,
    placeholderData: (prev) => prev,
  });
};

export const useFirstValuationDetail = (id: string | undefined) => {
  return useQuery<FirstValuationDto, ApiClientError>({
    queryKey: detailKey(id),
    queryFn: () => fetchFirstValuation(id!),
    enabled: !!id,
  });
};

/** Invalidate both the batch list and the per-valuation detail caches. */
const useInvalidate = () => {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: [FIRST_VALUATION_QUERY_KEY] });
};

export const useCreateFirstValuation = () => {
  const invalidate = useInvalidate();
  return useMutation<
    FirstValuationDto,
    ApiClientError,
    { batchId: string; body: CreateFirstValuationInput }
  >({
    mutationFn: ({ batchId, body }) => createFirstValuation(batchId, body),
    onSuccess: invalidate,
  });
};

/** File the caller's company's valuation. */
export const useSubmitValuation = () => {
  const invalidate = useInvalidate();
  return useMutation<FirstValuationDto, ApiClientError, { id: string; body: SubmitInput }>({
    mutationFn: ({ id, body }) => submitValuation(id, body),
    onSuccess: invalidate,
  });
};

export const useFinalizeFirstValuation = () => {
  const invalidate = useInvalidate();
  return useMutation<FirstValuationDto, ApiClientError, { id: string; body: FinalizeInput }>({
    mutationFn: ({ id, body }) => finalizeFirstValuation(id, body),
    onSuccess: invalidate,
  });
};

export const useLockFirstValuation = () => {
  const invalidate = useInvalidate();
  return useMutation<FirstValuationDto, ApiClientError, string>({
    mutationFn: (id) => lockFirstValuation(id),
    onSuccess: invalidate,
  });
};

export const useRequestRevaluation = () => {
  const invalidate = useInvalidate();
  return useMutation<FirstValuationDto, ApiClientError, string>({
    mutationFn: (id) => requestRevaluation(id),
    onSuccess: invalidate,
  });
};
